<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { useMenuImport } from '@/composition/menu-import/menu-import-context';
  import { useLiveRow } from '@/composition/menu-import/use-live-row';
  import type { ILiveLineAction } from '@/domain/menu-import/live-rows';
  import { addonRuleKey } from '@/domain/menu-import/menu-draft';
  import type { TRowDecoration } from '@/domain/menu-import/proposal-effects';
  import {
    itemReviewText,
    reviewLineText,
  } from '@/domain/menu-import/review-queue';
  import { itemSourcePageId } from '@/domain/menu-import/source-viewer';
  import {
    MAX_PRICE,
    currencySymbol,
    formatMoney,
    parsePriceInput,
  } from '@/domain/store-setup/money';
  import type {
    IDraftAddonGroup,
    IDraftItem,
    ILiveOther,
    IMenuDraft,
    TReviewLevel,
  } from '@/interfaces/IMenuImport';
  import { Globals } from '@/modules/Globals';
  import V2MenuItemEditor from './V2MenuItemEditor.vue';
  import V2MenuLiveItemEditor from './V2MenuLiveItemEditor.vue';
  import V2MenuLiveLine from './V2MenuLiveLine.vue';
  import V2MenuLivePrice from './V2MenuLivePrice.vue';
  import V2MenuLiveRowLines from './V2MenuLiveRowLines.vue';

  /**
   * One dish of the workspace list (plan §4.5 «Dish row»): status dot, name,
   * description, price (data style, «—» when missing), sizes and add-on
   * groups as badges, the photo, «Oculto». Under an amber dish «Revise: …»
   * with «Está bien»; under a red one «Falta el precio» with an inline price
   * and «Ocultar hasta tener precio». A pending proposal marks the row
   * («Nuevo», «Cambiado» with the old values struck, «Se quita»). A tap
   * outside its controls opens the dish's editor right below it; the root
   * wraps the card so the editor can follow it. In an update (a store with
   * dishes, plan §3.5) a dish already on the menu shows the menu's name and
   * prices (the new price below today's struck one) with its quiet
   * sub-lines, a new dish «Nuevo», and a dish to decide the amber live line.
   */
  const PRICE_MAX_LENGTH = 16;

  interface IDecorationBadge {
    color: 'success' | 'warning' | 'error';
    key: string;
  }

  /** «Nuevo», «Cambiado», «Se quita»: always a label, never colour only. */
  const DECORATION_BADGES: Readonly<
    Record<TRowDecoration, IDecorationBadge>
  > = {
    new: { color: 'success', key: 'menuImport.proposal.new' },
    changed: { color: 'warning', key: 'menuImport.proposal.changed' },
    removed: { color: 'error', key: 'menuImport.proposal.removed' },
  };

  const props = withDefaults(
    defineProps<{
      item: IDraftItem;
      /** The dish as it is now, when the row shows a proposed change of it. */
      beforeItem?: IDraftItem | null;
      decoration?: TRowDecoration | null;
      /** A dish a proposal would add: nothing to open or fix yet. */
      ghost?: boolean;
      /** The draft's add-on groups (a stable reference unless a group changes). */
      addonGroups: Record<string, IDraftAddonGroup>;
      /** The whole draft, given only while the editor is open (it lists categories and groups). */
      draft?: IMenuDraft | null;
      open: boolean;
      selected: boolean;
      currency: string;
      country: string;
      /** Review lines and inline fixes (not while reading). */
      showReview: boolean;
      /** The editor shows the allergens (some dish of the draft has them). */
      showAllergens?: boolean;
      /** Update sessions: the menu's dishes no dish is linked to (`draft.live.others`); else null. */
      liveOthers?: ILiveOther[] | null;
    }>(),
    {
      beforeItem: null,
      decoration: null,
      ghost: false,
      draft: null,
      showAllergens: false,
      liveOthers: null,
    }
  );
  const emit = defineEmits<{
    (e: 'open'): void;
    (e: 'confirm'): void;
    (e: 'set-price', price: number): void;
    (e: 'hide'): void;
    /** «Sí, es el mismo», a candidate: this dish is that dish of the menu. */
    (e: 'link', liveItemId: number): void;
    /** «No, es otro»: a new dish. */
    (e: 'new'): void;
    /** «Dejar {price}». */
    (e: 'keep-price'): void;
    /** «Dejar los de hoy». */
    (e: 'keep-sizes'): void;
    /** «Mostrarlo». */
    (e: 'show'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const ctx = useMenuImport();

  const priceInput = ref('');
  const priceError = ref(false);
  const photoFailed = ref(false);
  /** The inline price field has the focus: it stays on screen until the owner leaves it. */
  const priceFocused = ref(false);
  /** Mounted once needed, then only hidden: a delta never removes the field under the owner's fingers. */
  const priceFieldMounted = ref(false);

  /** The dot follows the dish as it is now, not a proposed version. */
  const level = computed<TReviewLevel>(() =>
    props.ghost ? 'ok' : (props.beforeItem ?? props.item).review.level
  );
  const reviewShown = computed(
    () => props.showReview && !props.ghost && props.decoration === null
  );
  /** The generic amber line; a dish with the live line gets only that one. */
  const showCheck = computed(
    () =>
      reviewShown.value && level.value === 'check' && liveLine.value === null
  );
  const showFix = computed(() => reviewShown.value && level.value === 'fix');
  /** The inline price; the open editor has its own price field. */
  const canTypePrice = computed(
    () =>
      showFix.value &&
      !props.open &&
      props.item.review.reason === 'missing_price' &&
      props.item.sizes.length === 0
  );
  /** Shown while it applies, and while the owner is typing in it. */
  const showPriceField = computed(
    () => canTypePrice.value || priceFocused.value
  );
  /**
   * «Revise: …» / «Falta el precio» / «Responda la pregunta de arriba», and
   * the allergens read while they are not confirmed («Revise los alérgenos:
   * Gluten y Lácteos»): «Está bien» confirms what the owner sees.
   */
  const reviewLines = computed(() => {
    const text = itemReviewText(props.item);
    if (text === null) return [];
    return text.lines.map((line) =>
      reviewLineText(line, tr, proxy.$i18n.locale)
    );
  });
  /** The editor edits the dish as it is now, never a proposed version of it. */
  const editorItem = computed(() => props.beforeItem ?? props.item);

  //#region Update sessions (§3.5)
  const {
    updateRow,
    liveState,
    liveView,
    liveLines,
    liveLine,
    isNewDish,
    liveSizeBadges,
    displayName,
    editorBox,
    liveEditor,
    editorKey,
    pickEditor,
    onEditorFocusOut,
  } = useLiveRow({
    item: () => props.item,
    editorItem: () => editorItem.value,
    liveOthers: () => props.liveOthers,
    reviewShown: () => reviewShown.value,
    open: () => props.open,
    money,
    tr,
  });

  function onLiveAction(action: ILiveLineAction) {
    if (action.kind === 'link' && action.liveItemId !== undefined)
      emit('link', action.liveItemId);
    else if (action.kind === 'new') emit('new');
    else if (action.kind === 'confirm') emit('confirm');
    else if (action.kind === 'keepPrice') emit('keep-price');
    else if (action.kind === 'keepSizes') emit('keep-sizes');
  }
  //#endregion

  function money(value: number | null): string {
    return value === null
      ? '—'
      : formatMoney(value, props.currency, props.country);
  }

  /** The price column: empty for a dish with sizes (their badges carry the prices). */
  function priceLabel(item: IDraftItem): string {
    return item.sizes.length > 0 ? '' : money(item.price);
  }

  const price = computed(() =>
    liveView.value !== null ? '' : priceLabel(props.item)
  );
  const oldName = computed(() =>
    props.beforeItem && props.beforeItem.name !== props.item.name
      ? props.beforeItem.name
      : null
  );
  const oldPrice = computed(() => {
    if (!props.beforeItem) return null;
    const before = priceLabel(props.beforeItem);
    return before !== '' && before !== price.value ? before : null;
  });
  const sizeBadges = computed(() =>
    (liveView.value !== null ? [] : props.item.sizes).map((size) => ({
      key: size.id,
      text: `${size.label} ${money(size.price)}`,
    }))
  );
  const groupBadges = computed(() =>
    (liveView.value !== null ? [] : props.item.addonGroupIds)
      .map((id) => props.addonGroups[id])
      .filter((group): group is IDraftAddonGroup => group !== undefined)
      .map((group) => {
        const rule = addonRuleKey(group);
        return {
          key: group.id,
          text: `${group.title} (${group.options.length}) · ${tr(
            rule.key,
            rule.params
          )}`,
        };
      })
  );
  /** A dish already on the menu keeps the menu's photo: the read one is never shown. */
  const photoUrl = computed(() =>
    props.item.photo && !photoFailed.value && liveView.value === null
      ? `${Globals.uploadsPath}/${props.item.photo}`
      : null
  );
  /** The right column: the price and the photo, when there is either. */
  const hasSide = computed(
    () =>
      price.value !== '' ||
      oldPrice.value !== null ||
      (liveView.value !== null && liveView.value.price !== null) ||
      photoUrl.value !== null
  );
  const decorationBadge = computed(() =>
    props.decoration === null ? null : DECORATION_BADGES[props.decoration]
  );
  const rowClasses = computed(() => [
    `level-${level.value}`,
    props.decoration ? `is-${props.decoration}` : '',
    {
      'is-hidden': props.item.hidden,
      'is-open': props.open,
      'is-selected': props.selected,
      'is-ghost': props.ghost,
      'is-unchanged': liveState.value === 'unchanged',
      'is-update': updateRow.value,
    },
  ]);

  function onPriceInput(text: string) {
    priceInput.value = text;
    priceError.value = false;
  }

  function onPriceFocus() {
    priceFocused.value = true;
  }

  function onPriceBlur() {
    priceFocused.value = false;
  }

  watch(
    showPriceField,
    (shown) => {
      if (shown) priceFieldMounted.value = true;
    },
    { immediate: true }
  );

  function blurActive() {
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  }

  /** «Guardar»: the typed price, parsed like every price the owner types. */
  function savePrice() {
    const value = parsePriceInput(priceInput.value, props.country);
    if (value === null || value > MAX_PRICE) {
      priceError.value = true;
      return;
    }
    priceError.value = false;
    // The row loses its price field once saved: let the phone keyboard go first.
    blurActive();
    emit('set-price', value);
  }

  // A new photo (or none) gets a fresh chance to load.
  watch(
    () => props.item.photo,
    () => {
      photoFailed.value = false;
    }
  );

  //#region Editor
  /** The photo page the dish was read from; null for typed, pasted or copied dishes. */
  const sourcePageId = computed(() =>
    props.open ? itemSourcePageId(editorItem.value, ctx.pages.value) : null
  );

  function closeEditor() {
    ctx.openItemId.value = null;
  }

  /** «Ver en la carta»: «Carta original» on the dish's page, the dish highlighted. */
  function seeSource() {
    const pageId = sourcePageId.value;
    if (pageId === null) return;
    ctx.viewerPageId.value = pageId;
    ctx.selectedItemId.value = editorItem.value.id;
    ctx.sidePanelTab.value = 'original';
    // Narrow screens show the side panel in the sheet (docked panes ignore it).
    ctx.sheetOpen.value = true;
  }
  //#endregion
</script>

<template>
  <div
    class="v2-menu-draft-item"
    :class="rowClasses"
    :data-item-id="ghost ? undefined : item.id"
  >
    <div class="item-card">
      <button
        v-if="!ghost"
        type="button"
        class="item-hit"
        :aria-label="displayName"
        :aria-expanded="open ? 'true' : 'false'"
        @click="emit('open')"
      />
      <span
        class="item-dot"
        role="img"
        :aria-label="$t(`menuImport.item.dot.${level}`)"
      />
      <div class="item-main">
        <p class="item-name">
          <s v-if="oldName !== null" class="was">{{ oldName }}</s>
          <span class="name-text">{{ displayName }}</span>
          <PosUiBadge
            v-if="decorationBadge"
            :color="decorationBadge.color"
            size="sm"
            >{{ $t(decorationBadge.key) }}</PosUiBadge
          >
          <PosUiBadge v-if="isNewDish" color="success" size="sm">{{
            $t('menuImport.proposal.new')
          }}</PosUiBadge>
          <PosUiBadge v-if="item.hidden && liveView === null" size="sm">{{
            $t('menuImport.item.hidden')
          }}</PosUiBadge>
        </p>
        <p
          v-if="item.description && liveView === null"
          class="item-description"
        >
          {{ item.description }}
        </p>
        <div
          v-if="
            sizeBadges.length > 0 ||
            groupBadges.length > 0 ||
            liveSizeBadges.length > 0
          "
          class="item-tags"
        >
          <PosUiBadge v-for="badge in sizeBadges" :key="badge.key" size="sm">{{
            badge.text
          }}</PosUiBadge>
          <span
            v-for="badge in liveSizeBadges"
            :key="badge.key"
            class="live-size"
          >
            <PosUiBadge size="sm" class="live-size-badge">
              <span :aria-hidden="badge.aria !== null ? 'true' : undefined"
                >{{ badge.label }}
                <s v-if="badge.was !== null" class="was">{{ badge.was }}</s>
                {{ badge.now }}</span
              >
              <span v-if="badge.aria !== null" class="sr-text"
                >{{ badge.label }}: {{ badge.aria }}</span
              >
            </PosUiBadge>
            <PosUiBadge v-if="badge.isNew" color="success" size="xs">{{
              $t('menuImport.proposal.new')
            }}</PosUiBadge>
          </span>
          <PosUiBadge
            v-for="badge in groupBadges"
            :key="badge.key"
            size="sm"
            class="group-badge"
            >{{ badge.text }}</PosUiBadge
          >
        </div>
      </div>
      <div v-if="hasSide" class="item-side">
        <p v-if="liveView && liveView.price" class="item-price">
          <V2MenuLivePrice
            :price="liveView.price"
            :currency="currency"
            :country="country"
          />
        </p>
        <p v-else-if="price !== '' || oldPrice !== null" class="item-price">
          <s v-if="oldPrice !== null" class="was">{{ oldPrice }}</s>
          <span>{{ price }}</span>
        </p>
        <img
          v-if="photoUrl"
          class="item-photo"
          :src="photoUrl"
          alt=""
          loading="lazy"
          @error="photoFailed = true"
        />
      </div>
      <V2MenuLiveRowLines
        v-if="liveLines"
        class="item-live-lines"
        :lines="liveLines"
        :currency="currency"
        :country="country"
        @show="emit('show')"
      />
      <V2MenuLiveLine
        v-if="liveLine"
        class="item-live-line"
        :model="liveLine"
        @action="onLiveAction"
      />
      <div v-if="showCheck" class="item-review is-check">
        <PosUiIcon name="alert-circle-outline" class="review-icon" />
        <span class="review-text">
          <span v-for="line in reviewLines" :key="line" class="review-line">{{
            line
          }}</span>
        </span>
        <button type="button" class="row-link" @click="emit('confirm')">
          {{ $t('menuImport.item.looksGood') }}
        </button>
      </div>
      <div v-if="showFix" class="item-review is-fix">
        <PosUiIcon name="alert-circle" class="review-icon" />
        <span class="review-text">
          <span v-for="line in reviewLines" :key="line" class="review-line">{{
            line
          }}</span>
        </span>
      </div>
      <!-- v-show once mounted: a delta (a proposal, a read) never unmounts the focused field. -->
      <div v-if="priceFieldMounted" v-show="showPriceField" class="item-fix">
        <PosUiInput
          class="price-input"
          :value="priceInput"
          :prefix="currencySymbol(currency)"
          :maxlength="PRICE_MAX_LENGTH"
          inputmode="decimal"
          autocomplete="off"
          :placeholder="$t('menuImport.item.pricePlaceholder')"
          :aria-label="$t('menuImport.editor.price')"
          @input="onPriceInput"
          @enter="savePrice"
          @focus="onPriceFocus"
          @blur="onPriceBlur"
        />
        <PosUiButton color="neutral" variant="outline" @click="savePrice">{{
          $t('menuImport.item.save')
        }}</PosUiButton>
        <button type="button" class="row-link is-quiet" @click="emit('hide')">
          {{ $t('menuImport.item.hideUntilPriced') }}
        </button>
        <p v-if="priceError" class="price-error" role="alert">
          {{ $t('menuImport.editor.priceInvalid') }}
        </p>
      </div>
    </div>
    <div
      v-if="open && draft && !ghost"
      ref="editorBox"
      class="item-editor-box"
      @focusout="onEditorFocusOut"
    >
      <V2MenuLiveItemEditor
        v-if="liveEditor"
        :key="editorKey"
        :item="editorItem"
        :draft="draft"
        :currency="currency"
        :country="country"
        :has-source="showReview && sourcePageId !== null"
        @see-source="seeSource"
        @relinked="pickEditor"
        @close="closeEditor"
      />
      <V2MenuItemEditor
        v-else
        :key="editorKey"
        :item="editorItem"
        :draft="draft"
        :currency="currency"
        :country="country"
        :show-allergens="showAllergens"
        :has-source="showReview && sourcePageId !== null"
        @see-source="seeSource"
        @relinked="pickEditor"
        @close="closeEditor"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-draft-item {
    @apply tw-flex tw-flex-col;
    animation: draft-row-in 150ms ease-out;

    .item-card {
      // No column gap: an empty right column (no price, no photo) must take no room.
      @apply tw-relative tw-grid tw-items-start tw-gap-y-1 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-3 tw-transition-colors;
      grid-template-columns: auto minmax(0, 1fr) auto;
    }
    // The whole card opens the dish; its own controls sit above this button.
    .item-hit {
      @apply tw-absolute tw-inset-0 tw-z-0 tw-m-0 tw-cursor-pointer tw-rounded-xl tw-border-0 tw-bg-transparent tw-p-0;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .item-dot,
    .item-main,
    .item-side {
      @apply tw-pointer-events-none tw-relative;
    }
    .item-dot {
      @apply tw-mt-2 tw-h-2 tw-w-2 tw-rounded-full tw-bg-neutral-300;
    }
    &.level-check .item-dot {
      @apply tw-bg-amber-500;
    }
    &.level-fix .item-dot {
      @apply tw-bg-red-500;
    }
    .item-main {
      @apply tw-ml-3 tw-flex tw-min-w-0 tw-flex-col tw-gap-1;
    }
    .item-side {
      @apply tw-ml-3 tw-flex tw-items-start tw-gap-3;
    }
    .item-name {
      @apply tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-base tw-font-semibold tw-text-neutral-900;
    }
    .name-text {
      @apply tw-min-w-0 tw-break-words;
    }
    .was {
      @apply tw-font-normal tw-text-neutral-400;
    }
    .item-description {
      @apply tw-m-0 tw-overflow-hidden tw-text-sm tw-text-neutral-500;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
    .item-tags {
      @apply tw-flex tw-flex-wrap tw-gap-1.5 tw-pt-0.5;
      .group-badge {
        @apply tw-max-w-full tw-whitespace-normal;
      }
    }
    .item-price {
      @apply tw-m-0 tw-flex tw-flex-col tw-items-end tw-whitespace-nowrap tw-text-base tw-font-semibold tw-tabular-nums tw-text-neutral-900;
      .was {
        @apply tw-text-sm;
      }
    }
    .item-photo {
      @apply tw-h-14 tw-w-14 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-object-cover;
    }
    .item-live-lines,
    .item-live-line {
      @apply tw-relative tw-z-[1] tw-ml-3 tw-pt-0.5;
      grid-column: 2 / -1;
    }
    // A size and, when only the photo has it, its «Nuevo».
    .live-size {
      @apply tw-inline-flex tw-max-w-full tw-items-center tw-gap-1;
    }
    .live-size-badge {
      @apply tw-max-w-full tw-whitespace-normal;
      .was {
        @apply tw-text-neutral-400;
      }
    }
    .sr-text {
      @apply tw-sr-only;
    }
    .item-review,
    .item-fix {
      @apply tw-relative tw-z-[1] tw-ml-3 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-sm;
      grid-column: 2 / -1;
    }
    .item-review {
      .review-icon {
        @apply tw-shrink-0 tw-text-base;
      }
      // One line per thing to check (the reason, the allergens read).
      .review-text {
        @apply tw-flex tw-min-w-0 tw-flex-col;
      }
      &.is-check {
        @apply tw-text-amber-700;
      }
      &.is-fix {
        @apply tw-font-medium tw-text-red-700;
      }
    }
    .item-fix {
      @apply tw-gap-2 tw-pt-1;
      .price-input {
        @apply tw-w-40 tw-shrink-0;
      }
    }
    .row-link {
      @apply tw-min-h-11 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4;
      &.is-quiet {
        @apply tw-font-normal tw-text-neutral-500;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .price-error {
      @apply tw-m-0 tw-w-full tw-text-xs tw-text-red-600;
    }

    &.is-hidden .item-card {
      @apply tw-opacity-60;
    }
    // An update: the dish looks faded; the lines and buttons that decide it stay readable.
    &.is-hidden.is-update .item-card {
      @apply tw-opacity-100;
      .item-dot,
      .item-main,
      .item-side {
        @apply tw-opacity-60;
      }
    }
    // Already on the menu, nothing changes: quieter.
    &.is-unchanged {
      .item-name {
        @apply tw-font-medium tw-text-neutral-600;
      }
      .item-price {
        @apply tw-text-neutral-600;
      }
    }
    &.is-open .item-card {
      @apply tw-border-neutral-400 tw-shadow-sm;
    }
    // Active-state indicator: the dish picked in «Carta original».
    &.is-selected .item-card {
      @apply tw-border-orange-600 tw-ring-2 tw-ring-orange-100;
      animation: draft-row-flash 900ms ease-out;
    }
    // Pending proposal marks: a label on every one, never colour only.
    &.is-new .item-card {
      @apply tw-border-emerald-200 tw-bg-emerald-50;
    }
    &.is-changed .item-card {
      @apply tw-border-amber-300 tw-bg-amber-50;
    }
    &.is-removed .item-card {
      @apply tw-border-red-200 tw-bg-red-50;
      .name-text,
      .item-price {
        @apply tw-line-through tw-decoration-red-400;
      }
    }
    &.is-ghost .item-card {
      @apply tw-border-dashed;
    }
  }

  @keyframes draft-row-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes draft-row-flash {
    0%,
    40% {
      background-color: theme('colors.orange.50');
    }
    100% {
      background-color: theme('colors.white');
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-menu-draft-item,
    .v2-menu-draft-item.is-selected .item-card {
      animation: none;
    }
  }
</style>
